import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import { CURRENCIES } from '@/utils/format';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';

const ICON_OPTIONS = ['🏪', '💼', '🏢', '🛍️', '🦒', '🚀', '🎨', '🔧', '📦', '🍕', '☕', '🏠'];
const COLOR_OPTIONS = ['#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6', '#f97316'];

export default function AddBusinessModal({ open, onClose, business = null }) {
  const { t } = useTranslation();
  const addBusiness = useStore((s) => s.addBusiness);
  const updateBusiness = useStore((s) => s.updateBusiness);

  const isEdit = !!business;

  const [name, setName] = useState('');
  const [icon, setIcon] = useState('💼');
  const [color, setColor] = useState('#3b82f6');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && business) {
      setName(business.name || '');
      setIcon(business.icon || '💼');
      setColor(business.color || '#3b82f6');
      setDescription(business.description || '');
      setCurrency(business.currency || 'USD');
      setErrors({});
    } else if (open) {
      setName('');
      setIcon('💼');
      setColor('#3b82f6');
      setDescription('');
      setCurrency('USD');
      setErrors({});
    }
  }, [open, business]);

  const handleSave = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('business.form.nameRequired');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const data = { name: name.trim(), icon, color, description: description.trim(), currency };
    if (isEdit) {
      updateBusiness(business.id, data);
    } else {
      addBusiness(data);
    }
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('business.editBusiness') : t('business.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('business.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('business.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('business.form.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 scale-110'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('business.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900 scale-110' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Currency */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('business.form.currency')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  currency === c.code
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {c.code}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('business.form.description')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('business.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

      </div>
    </Modal>
  );
}
